/**
 * Assignments API
 *
 * Functions for interacting with the assignments endpoints.
 */

import { api } from './client';

// =============================================================================
// Types (re-exported from centralized models)
// =============================================================================

export type {
  AssignmentStatus,
  AvailabilityStatus,
  Assignment,
  RoleWithAssignments,
  AvailableCrew,
} from '../types/models';

import type { AssignmentStatus, Assignment, RoleWithAssignments, AvailableCrew } from '../types/models';

// =============================================================================
// Request/Response Types
// =============================================================================

export interface ListAssignmentsParams {
  eventId?: string;
  userId?: string;
  status?: AssignmentStatus;
  page?: number;
  limit?: number;
}

export interface ListAssignmentsResponse {
  assignments: Assignment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateAssignmentInput {
  userId: string;
}

export interface UpdateAssignmentInput {
  status?: AssignmentStatus;
  declineReason?: string | null;
}

export interface UpdateAssignmentHoursInput {
  hoursWorked?: number | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
}

export interface RespondToAssignmentInput {
  status: 'CONFIRMED' | 'DECLINED';
  declineReason?: string | null;
}

// =============================================================================
// Assignment CRUD
// =============================================================================

/**
 * List all assignments with optional filters
 */
export async function listAssignments(params?: ListAssignmentsParams): Promise<ListAssignmentsResponse> {
  const response = await api.get<ListAssignmentsResponse>('/assignments', { params });
  return response.data;
}

/**
 * Get a single assignment by ID
 */
export async function getAssignment(id: string): Promise<{ assignment: Assignment }> {
  const response = await api.get<{ assignment: Assignment }>(`/assignments/${id}`);
  return response.data;
}

/**
 * Update an assignment (admin)
 */
export async function updateAssignment(
  id: string,
  data: UpdateAssignmentInput
): Promise<{ message: string; assignment: Assignment }> {
  const response = await api.put<{ message: string; assignment: Assignment }>(
    `/assignments/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete an assignment
 */
export async function deleteAssignment(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/assignments/${id}`);
  return response.data;
}

/**
 * Update hours worked for an assignment (admin)
 */
export async function updateAssignmentHours(
  id: string,
  data: UpdateAssignmentHoursInput
): Promise<{ message: string; assignment: Assignment }> {
  const response = await api.put<{ message: string; assignment: Assignment }>(
    `/assignments/${id}/hours`,
    data
  );
  return response.data;
}

// =============================================================================
// Crew Response
// =============================================================================

/**
 * Crew member responds to assignment (confirm/decline)
 */
export async function respondToAssignment(
  id: string,
  data: RespondToAssignmentInput
): Promise<{ message: string; assignment: Assignment }> {
  const response = await api.post<{ message: string; assignment: Assignment }>(
    `/assignments/${id}/respond`,
    data
  );
  return response.data;
}

/**
 * Get current user's assignments
 */
export async function getMyAssignments(params?: {
  status?: AssignmentStatus;
  upcoming?: boolean;
}): Promise<{ assignments: Assignment[] }> {
  const response = await api.get<{ assignments: Assignment[] }>('/assignments/my', {
    params: { ...params, upcoming: params?.upcoming ? 'true' : undefined },
  });
  return response.data;
}

// =============================================================================
// Event-specific Assignment Functions
// =============================================================================

/**
 * Get all assignments for an event (grouped by role)
 */
export async function getEventAssignments(eventId: string): Promise<{ roles: RoleWithAssignments[] }> {
  const response = await api.get<{ roles: RoleWithAssignments[] }>(
    `/events/${eventId}/assignments`
  );
  return response.data;
}

/**
 * Get available crew for a role on an event
 */
export async function getAvailableCrew(
  eventId: string,
  roleId: string
): Promise<{ crew: AvailableCrew[] }> {
  const response = await api.get<{ crew: AvailableCrew[] }>(
    `/events/${eventId}/roles/${roleId}/available-crew`
  );
  return response.data;
}

/**
 * Assign a crew member to a role
 */
export async function assignCrewToRole(
  eventId: string,
  roleId: string,
  userId: string
): Promise<{ message: string; assignment: Assignment }> {
  const response = await api.post<{ message: string; assignment: Assignment }>(
    `/events/${eventId}/roles/${roleId}/assignments`,
    { userId }
  );
  return response.data;
}

// =============================================================================
// Pending Notifications (per crew member)
// =============================================================================

export interface PendingNotificationGroup {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  };
  assignments: Array<{
    id: string;
    status: AssignmentStatus;
    assignedAt: string;
    event: {
      id: string;
      name: string;
      venue: string;
      eventDate: string;
      callTime?: string | null;
      startTime?: string | null;
      status: string;
    };
    role: {
      id: string;
      roleName: string;
    };
  }>;
}

export async function listPendingNotifications(): Promise<{ groups: PendingNotificationGroup[] }> {
  const response = await api.get<{ groups: PendingNotificationGroup[] }>(
    '/assignments/pending-notifications'
  );
  return response.data;
}

export async function notifyUser(userId: string): Promise<{
  message: string;
  count: number;
  queued: boolean;
}> {
  const response = await api.post<{
    message: string;
    count: number;
    queued: boolean;
  }>('/assignments/notify-user', { userId });
  return response.data;
}

/**
 * With eventId, only crew who have a pending assignment on that event are
 * emailed — but each email still covers ALL of that person's pending
 * assignments across events (consolidation).
 */
export async function notifyAllPending(eventId?: string): Promise<{
  message: string;
  usersQueued: number;
  assignmentsPending: number;
  queued: boolean;
}> {
  const response = await api.post<{
    message: string;
    usersQueued: number;
    assignmentsPending: number;
    queued: boolean;
  }>('/assignments/notify-all-pending', eventId ? { eventId } : undefined);
  return response.data;
}

export interface RemovePendingInput {
  userId?: string;
  assignmentIds?: string[];
}

export async function dismissPendingNotifications(
  input: RemovePendingInput
): Promise<{ message: string; count: number }> {
  const response = await api.post<{ message: string; count: number }>(
    '/assignments/dismiss-notifications',
    input
  );
  return response.data;
}

export async function cancelPendingAssignments(
  input: RemovePendingInput
): Promise<{ message: string; count: number }> {
  const response = await api.post<{ message: string; count: number }>(
    '/assignments/cancel-pending',
    input
  );
  return response.data;
}
